import { TaxRateUpdateParamsV2, TaxRateV2, TaxRatesApi } from '@citruscamps/citrus-client'
import { useQueryClient } from 'react-query'
import { ROUTES } from '../../../../../constants/routes'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { useRouter } from '../../../../../hooks/useRouter'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'

interface IProps {
  programId?: string
}

interface IUpdateTaxRate {
  handleUpdate: (value: TaxRateUpdateParamsV2) => Promise<void>
  handleDelete: (value: TaxRateV2) => Promise<void>
}

export const useUpdateTaxRateV2 = ({ programId }: IProps): IUpdateTaxRate => {
  const { requestHandler } = useRequestHandler()
  const router = useRouter()
  const { setToast } = useToast()
  const queryClient = useQueryClient()
  const handleUpdate = async (value: TaxRateUpdateParamsV2): Promise<void> => {
    if (programId && value.id) {
      const client = new TaxRatesApi(generateApiConfig())
      await requestHandler<TaxRateV2>(
        () =>
          client.updateTaxRateV2({
            program_id: programId,
            tax_rate_id: value.id,
            TaxRateUpdateParamsV2: value,
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'tax_rate',
          program_id: programId,
        }),
      )
      await router.push(ROUTES.FINANCIALS_TAX_RATES)
    }
  }

  const handleDelete = async (value: TaxRateV2) => {
    const taxRateClient = new TaxRatesApi(generateApiConfig())
    if (programId && value?.id) {
      try {
        await requestHandler(
          () => taxRateClient.deleteTaxRateV2({ program_id: programId, tax_rate_id: value.id }),
          {
            toast: {
              setToast,
              success: (translations as any)['success.default_toast_message'],
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'tax_rate',
            program_id: programId,
          }),
        )
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  return {
    handleUpdate,
    handleDelete,
  }
}
